import React, { useEffect, useState, useMemo } from "react"
import { createPortal } from "react-dom"
import classnames from "classnames"
import "./styles.scss"
import gradient, { gradientColors } from "../utils/gradient"
import Map from "../Map/"
import { getCountryByName } from "../utils/countryLookup"
import DataLoader, { years } from "../DataLoader"

import arrowDown from "../images/arrow-down.svg"
import arrowUp from "../images/arrow-up.svg"
import { useSmallWidthMode } from "../utils/smallWidthMode"
import HistoricalBubbleGraph from "../HistoricalBubbleGraph/"
import { percentChangeColorScale } from "../Overview/"
import { useMeasure, useWindowScroll } from "react-use"

const scaleBarWidth = 200
const scaleBarWidthSmallMode = 100
const scaleBarMargins = {
  top: 10,
  left: 26,
  right: 30,
  bottom: 10,
}
const scaleBarHeight = 7
const mapSize = 90

export default function Tooltip(props) {
  const {
    country,
    x,
    y,
    scoreKey,
    selectedYear,
    colorByPercentDifferenceData,
  } = props
  const windowScroll = useWindowScroll()
  const [wrapperRef, wrapperMeasure] = useMeasure()
  const [isVisible, setIsVisible] = useState(false)
  const [clickInstructionsVisible, setClickInstructionsVisible] =
    useState(false)

  const inSmallMode = useSmallWidthMode()

  // Memoize tooltip element lookup to prevent unnecessary querying
  const tooltipEle = useMemo(() => document.querySelector("#tooltip"), [])

  // Memoize the country data lookup to avoid recalculating every render
  const mapCountryData = useMemo(
    () => getCountryByName(country.Country),
    [country],
  )

  // Calculate score and color just once
  const score = useMemo(() => {
    if (colorByPercentDifferenceData) {
      return country.externalPercentChange !== null
        ? country.externalPercentChange
        : null
    }
    return country[scoreKey]
  }, [country, scoreKey, colorByPercentDifferenceData])

  const scoreColor = useMemo(
    () =>
      colorByPercentDifferenceData
        ? percentChangeColorScale(score)
        : gradient(score),
    [colorByPercentDifferenceData, score],
  )

  // Calculate factorIndexLabel based on scoreKey
  const factorIndexLabel = useMemo(() => {
    const scoreKeyMatch = scoreKey.match(/^[0-9]\.[0-9]/)
    if (scoreKey.indexOf("Factor ") === 0) {
      return scoreKey.split(":")[0]
    } else if (scoreKeyMatch) {
      return `Subfactor ${scoreKeyMatch[0]}`
    }
    return "Index"
  }, [scoreKey])

  useEffect(() => {
    if (props.tooltipMessage === null) return

    const showTimeout = setTimeout(() => setIsVisible(true), 100) // Delay show by 0.1s
    const clickInstructionsTimeout = setTimeout(
      () => setClickInstructionsVisible(true),
      250,
    ) // Show click instructions after 0.25s

    return () => {
      clearTimeout(showTimeout)
      clearTimeout(clickInstructionsTimeout)
      setIsVisible(false)
    }
  }, [props.tooltipMessage])

  const hideTooltip = () => setIsVisible(false) // Simplified hideTooltip with no fade-out logic, as CSS handles it

  // Precompute the position and flip logic
  const _x = x + 12
  const _y = y - 60 + windowScroll.y
  const flipX = _x > window.innerWidth - wrapperMeasure.width
  const flipY = _y > window.innerHeight - wrapperMeasure.height

  // Tooltip style
  const tooltipStyle = {
    left: _x + (flipX ? -20 : 0),
    top: _y + (flipY ? -20 : 0),
    transform: `translate(${flipX ? "-100%" : "0"}, ${flipY ? "-100%" : "0"})`,
    opacity: isVisible ? 1 : 0, // Controlled by CSS transition for fade-in/out
    transition: "opacity 0.2s cubic-bezier(0.77, 0, 0.175, 1)", // Smooth fade with EaseInOutQuart effect
  }

  // Precompute ranking icons for optimizations
  const previousYearData = useMemo(() => {
    const selectedYearIndex = years.indexOf(`${selectedYear}`)
    if (selectedYearIndex === 0) return null

    const previousYear = years[selectedYearIndex - 1]
    return DataLoader.dataByYear[previousYear].countries.find(
      (c) => c.Country === country.Country,
    )
  }, [selectedYear, country])

  const icons = useMemo(() => {
    let overallCOTIcon = null,
      globalRankCOTIcon = null,
      regionalRankCOTIcon = null,
      incomeRankCOTIcon = null

    if (previousYearData) {
      const previousYearScore = previousYearData[scoreKey]
      const globalRankIndexedByLastYearsCountriesOnly =
        country[`${scoreKey}_Rank_Global_lastYear`]
      const regionRankIndexedByLastYearsCountriesOnly =
        country[`${scoreKey}_Rank_Region_lastYear`]
      const incomeRankIndexedByLastYearsCountriesOnly =
        country[`${scoreKey}_Rank_Income Group_lastYear`]

      if (previousYearScore == null) {
        // no score last year (blank 2015/2016 sub-factor): no arrow
      } else if (score > previousYearScore)
        overallCOTIcon = (
          <img
            src={arrowUp}
            className="updownArrow"
            width={12}
            height={13}
            alt=""
          />
        )
      else if (score < previousYearScore)
        overallCOTIcon = (
          <img
            src={arrowDown}
            className="updownArrow"
            width={12}
            height={13}
            alt=""
          />
        )

      if (
        previousYearData[`${scoreKey}_Rank_Global`] <
        globalRankIndexedByLastYearsCountriesOnly
      )
        globalRankCOTIcon = (
          <img
            src={arrowDown}
            className="updownArrow"
            width={12}
            height={13}
            alt=""
          />
        )
      else if (
        previousYearData[`${scoreKey}_Rank_Global`] >
        globalRankIndexedByLastYearsCountriesOnly
      )
        globalRankCOTIcon = (
          <img
            src={arrowUp}
            className="updownArrow"
            width={12}
            height={13}
            alt=""
          />
        )

      if (
        previousYearData[`${scoreKey}_Rank_Region`] <
        regionRankIndexedByLastYearsCountriesOnly
      )
        regionalRankCOTIcon = (
          <img
            src={arrowDown}
            className="updownArrow"
            width={12}
            height={13}
            alt=""
          />
        )
      else if (
        previousYearData[`${scoreKey}_Rank_Region`] >
        regionRankIndexedByLastYearsCountriesOnly
      )
        regionalRankCOTIcon = (
          <img
            src={arrowUp}
            className="updownArrow"
            width={12}
            height={13}
            alt=""
          />
        )

      if (
        previousYearData[`${scoreKey}_Rank_Income Group`] <
        incomeRankIndexedByLastYearsCountriesOnly
      )
        incomeRankCOTIcon = (
          <img
            src={arrowDown}
            className="updownArrow"
            width={12}
            height={13}
            alt=""
          />
        )
      else if (
        previousYearData[`${scoreKey}_Rank_Income Group`] >
        incomeRankIndexedByLastYearsCountriesOnly
      )
        incomeRankCOTIcon = (
          <img
            src={arrowUp}
            className="updownArrow"
            width={12}
            height={13}
            alt=""
          />
        )
    }

    return {
      overallCOTIcon,
      globalRankCOTIcon,
      regionalRankCOTIcon,
      incomeRankCOTIcon,
    }
  }, [previousYearData, score, scoreKey, country])

  const scaleBarWidthToUse = inSmallMode
    ? scaleBarWidthSmallMode
    : scaleBarWidth

  return isVisible && score !== null && mapCountryData
    ? createPortal(
        <div className="tooltipWrapper" style={tooltipStyle} ref={wrapperRef}>
          <div className="tooltip">
            <div className="countryName fontTitle">{country.Country}</div>
            <div className="content">
              <div className="left">
                <svg
                  style={{ opacity: colorByPercentDifferenceData ? 0 : null }}
                  className="scaleBar"
                  width={
                    scaleBarWidthToUse +
                    scaleBarMargins.left +
                    scaleBarMargins.right
                  }
                  height={
                    scaleBarHeight +
                    scaleBarMargins.top +
                    scaleBarMargins.bottom
                  }
                >
                  <defs>
                    <linearGradient id="tooltipGradient">
                      {gradientColors.map((color) => (
                        <stop
                          key={color.offset}
                          offset={`${color.offset}%`}
                          stopColor={color.hex}
                        />
                      ))}
                    </linearGradient>
                    <mask id="tooltipMask">
                      <rect
                        width={scaleBarWidthToUse * score}
                        height={scaleBarHeight}
                        fill="#fff"
                      />
                    </mask>
                  </defs>
                  <g
                    transform={`translate(${scaleBarMargins.left}, ${scaleBarMargins.top})`}
                  >
                    <rect
                      width={scaleBarWidthToUse}
                      height={scaleBarHeight}
                      fill="#56565A"
                    />
                    <rect
                      width={scaleBarWidthToUse}
                      height={scaleBarHeight}
                      fill="url(#tooltipGradient)"
                      mask="url(#tooltipMask)"
                    />
                    <circle
                      cx={scaleBarWidthToUse * score}
                      cy={scaleBarHeight / 2}
                      r={8}
                      fill={scoreColor}
                      stroke="#CECECE"
                      strokeWidth={2}
                    />
                    <text y={scaleBarHeight / 2} x={-6} textAnchor="end">
                      0.0
                    </text>
                    <text y={scaleBarHeight / 2} x={scaleBarWidthToUse + 6}>
                      1.0
                    </text>
                  </g>
                </svg>
                <div className="mapAndStats">
                  <Map
                    id="tooltip"
                    width={mapSize}
                    height={mapSize}
                    visibleCountries={[mapCountryData.id]}
                    data={[
                      {
                        code: mapCountryData.code,
                        value: score,
                        overrideFill: props.colorByPercentDifferenceData
                          ? scoreColor
                          : null,
                      },
                    ]}
                    style={{ marginLeft: "1em", marginRight: "1em" }}
                    colorByPercentDifferenceData={
                      props.colorByPercentDifferenceData
                    }
                  />
                  <div>
                    <div className="groupLabel">
                      <span className="fontCaps">Region</span>{" "}
                      <span className="groupValue">{country.Region}</span>
                    </div>
                    <div className="groupLabel">
                      <span className="fontCaps">Income Group</span>{" "}
                      <span className="groupValue">
                        {country["Income Group"]}
                      </span>
                    </div>
                    <div className="ranking">
                      <span className="fontCaps globalLabel">
                        Global ranking
                      </span>
                      <span className="globalValue">
                        {country[`${scoreKey}_Rank_Global`]} /{" "}
                        {country.Count_Global}{" "}
                        {
                          //icons.globalRankCOTIcon
                        }
                      </span>
                    </div>
                    <div className="ranking">
                      <span className="fontCaps groupRankingLabel">
                        Regional Ranking
                      </span>
                      <span className="groupRankingValue">
                        {country[`${scoreKey}_Rank_Region`]} /{" "}
                        {country.Count_Region}{" "}
                        {
                          //icons.regionalRankCOTIcon
                        }
                      </span>
                    </div>
                    <div className="ranking">
                      <span className="fontCaps groupRankingLabel">
                        Income Ranking
                      </span>
                      <span className="groupRankingValue">
                        {country[`${scoreKey}_Rank_Income Group`]} /{" "}
                        {country["Count_Income Group"]}{" "}
                        {
                          //icons.incomeRankCOTIcon
                        }
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="right">
                <div style={{ backgroundColor: scoreColor }} className="score">
                  {colorByPercentDifferenceData ? (
                    <div>{score}%</div>
                  ) : (
                    <React.Fragment>
                      {score.toFixed(2)} {icons.overallCOTIcon}
                    </React.Fragment>
                  )}
                </div>
                <div className="scoreLabel">
                  {colorByPercentDifferenceData ? (
                    <React.Fragment>
                      Overall score
                      <br />
                      changes
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      {factorIndexLabel} Score
                      <br />
                      {selectedYear}
                    </React.Fragment>
                  )}
                </div>
              </div>
            </div>
            {props.showHistoricalPlot && (
              <div className="bubbleGraphWrapper">
                <div className="bubbleGraph">
                  <HistoricalBubbleGraph
                    selectedCountry={country.Country}
                    scoreKey={scoreKey}
                    smallVersion={true}
                  />
                </div>
              </div>
            )}
            <div className="tooltipControls fontCaps">
              <div className="detailsButton" onClick={props.gotoDetails}>
                Details
              </div>
              <div className="closeButton" onClick={hideTooltip}>
                Close
              </div>
            </div>
          </div>
          {clickInstructionsVisible && (
            <div
              className={classnames("clickInstructions", {
                visible: clickInstructionsVisible,
                flipX,
              })}
            >
              Click for {country.Country} Insights
            </div>
          )}
        </div>,
        tooltipEle,
      )
    : null
}

Tooltip.defaultProps = {
  closeTooltip: () => {},
  gotoDetails: () => {},
}
